// ============================================================
// effectiveProfile - the effective profile card's derived readouts
// ============================================================
// The pieces of a weapon's effective profile that are not attackCalc's:
// rate of fire as text, the damage bonus and how it is written, the
// re-roll talents that apply, the melee attack modes, the psy rating.

import type { Slot } from '../types';
import type { AttackerCombatStats } from '../engine/character/types';

/** Format rate of fire as a compact string like "S/3/–". */
export function formatRoF(slot: Slot): string {
  const { single, semi, full } = slot.rateOfFire;
  return [single ? 'S' : '–', semi > 0 ? String(semi) : '–', full > 0 ? String(full) : '–'].join('/');
}

/** The character's damage bonus for this kind of attack; 0 with no character. */
export function damageBonus(stats: AttackerCombatStats | null, isMelee: boolean): number {
  if (!stats) return 0;
  return isMelee ? stats.damageBonusMelee : stats.damageBonusRanged;
}

/** The bonus appended to a damage expression: "+3", "-1", or nothing; `spaced` writes " + 3" / " -1". */
export function damageSuffix(bonus: number, spaced = false): string {
  if (bonus > 0) return spaced ? ` + ${bonus}` : `+${bonus}`;
  if (bonus < 0) return spaced ? ` ${bonus}` : String(bonus);
  return '';
}

/** A re-roll talent pill. */
export interface RerollTalent {
  label: string;
  color: string;
}

// Each talent, and when it applies to this attack.
const REROLLS: {
  label: string;
  color: string;
  applies: (s: AttackerCombatStats, melee: boolean, aoa: boolean) => boolean;
}[] = [
  { label: 'Blademaster', color: '#c4a35a', applies: (s, melee) => !!s.hasBlademaster && melee },
  { label: 'Sure Strike', color: '#c4a35a', applies: (s, melee) => !!s.hasSureStrike && !melee },
  {
    label: 'Furious Assault',
    color: '#f59e0b',
    applies: (s, melee, aoa) => !!s.hasFuriousAssault && melee && aoa,
  },
  { label: 'Hunter of Aliens', color: '#a78bfa', applies: (s) => !!s.hasHunterOfAliens },
  { label: 'Scourge of Heretics', color: '#a78bfa', applies: (s) => !!s.hasScourgeOfHeretics },
];

/** The re-roll talents that apply to this attack, in pill order. */
export function rerollTalents(
  stats: AttackerCombatStats | null,
  isMelee: boolean,
  allOutAttack: boolean,
): RerollTalent[] {
  if (!stats) return [];
  return REROLLS.filter((r) => r.applies(stats, isMelee, allOutAttack)).map(({ label, color }) => ({ label, color }));
}

/**
 * The melee attack modes. THE SLOT, not the character (v8.364.6): the
 * slot's flag is the resolved value the fire-mode buttons read (a
 * per-weapon override may differ from the talents), so the card and the
 * buttons agree.
 */
export function attackModes(slot: Slot): string[] {
  return ['Standard', slot.canSwiftAttack && 'Swift', slot.canLightningAttack && 'Lightning'].filter((m): m is string =>
    Boolean(m),
  );
}

/** The psy rating the attack uses: the character's, else the slot's. */
export function psyRatingOf(stats: AttackerCombatStats | null, slot: Slot): number {
  return stats ? stats.psyRating : slot.psyRating;
}
